// The plan layer (build/CLIENT-FLOW2.md, Engineer B): POST /plan calls, one shared store per plan so the menu, the
// tray and the itinerary always show the same plan, optimistic adds, menu filters, calendar export and link-outs.
// Screens never call /plan directly.
import { useEffect, useSyncExternalStore } from 'react'
import { calendarLocation } from './calLocation'
import * as Sentry from '@sentry/react'
import { ApiError, ensureSession, supabase, track, type Item } from './api'
import { type Weekly } from './explore'
import { errorMessage } from './format'
import { buildCalendarInvite, googleInviteUrl, type InviteStop } from './calendarInvite'
import { APP_URL, SUPABASE_ANON_KEY, SUPABASE_URL, fn } from '../config'
import { f2 } from '../copyFlow2'

// ---------- types (supabase/functions/plan/index.ts) ----------
export type PlanItemData = Item & { hours?: Weekly | null; hours_text?: string | null; schedule_text?: string | null }
export type Dest = {
  scope?: string; label?: string; areas?: string[]; lat?: number; lng?: number; covered?: boolean; radius_km?: number
  /** WP13: the city's slug and IANA zone; `building` while the server sets the city up; `need_city` for a country or region. */
  city?: string; tz?: string; building?: boolean; need_city?: boolean
}
export type Party = { kind?: 'solo' | 'two' | 'group'; size?: number; with?: string; kids_ages?: number[] }
export type Plan = {
  id: string; user_id: string; couple_id: string | null; title: string | null; party: Party | null; dest: Dest | null
  start_date: string; end_date: string; params: Record<string, unknown> | null; status: string; version: number
  share_token?: string | null; mine?: boolean
}
export type Leg = 'walk' | 'drive' | 'ride' | 'transit'
export type Travel = { leg: Leg; leg_min: number }
/** A drop-in event's opening window (a long open house, a market): the plan holds a shorter visit inside it. */
export type Window = { open: string; close: string }
export type StopRef = { id: string; name: string; kind: string; address: string | null; neighborhood: string | null; url: string | null; lat: number | null; lng: number | null }
export type PlanStop = {
  id: string; day: string; name: string; start: string | null; end: string | null; approx: boolean
  kind: 'item' | 'stop' | 'custom'; category: string | null; address: string | null; url: string | null
  lat: number | null; lng: number | null; note: string | null
  item: PlanItemData | null; stop: StopRef | null; custom: OwnBlock | null
  travel?: Travel; conflict?: true; window?: Window | null
  /** The stop the server measured `travel` from (set on load): a travel line shows only beside that same stop (R8). */
  travelFrom?: string | null
}
/** A block of their own (R4, itinerary_v2): `type` labels it; `source` is 'manual' (a later email import adds its own). */
export type OwnType = 'food' | 'reservation' | 'travel' | 'stop' | 'other'
export type OwnBlock = { title: string; address: string | null; url: string | null; type?: OwnType | null; source?: string }
export type PlanDay = { date: string; items: PlanStop[] }
export type MenuCard = {
  score: number; fit: string[]; loved: boolean; added: boolean; km: number | null
  suggested: { start: string; end: string; approx: boolean } | null; item: PlanItemData; window?: Window | null
  /** The area `km` is measured from (where they start), when the server sends it. */
  from?: string | null
}
/** `top` (R8): up to 10 best fits across dated events and any-day places, in order; the menu opens with them. */
export type Menu = { covered: boolean; place?: string; top: MenuCard[]; days: { date: string; items: MenuCard[] }[]; anytime: MenuCard[]; per_day: number }
export type SharedStop = Pick<PlanStop, 'name' | 'start' | 'end' | 'approx' | 'kind' | 'category' | 'address' | 'url' | 'lat' | 'lng' | 'travel' | 'conflict'>
export type SharedPlanData = {
  plan: { title: string | null; start_date: string; end_date: string; place: string | null; tz?: string | null }; days: { date: string; items: SharedStop[] }[]
  /** WP16: a plan without dates shares its list (no day, no times). */
  unscheduled?: { name: string; category: string | null; address: string | null; url: string | null }[]
}
export type AddReq = { item?: string; stop?: string; custom?: { title: string; address?: string; url?: string; type?: OwnType }; note?: string; day?: string; start?: string; end?: string }
export type AddRes = { plan_item: { id: string; day: string; start_at: string | null; end_at: string | null; approx: boolean }; conflict: boolean; check_hours: boolean }

// ---------- calls ----------
export async function callFn<T>(name: string, body: Record<string, unknown>): Promise<T> {
  await ensureSession()
  const { data, error } = await supabase.functions.invoke(fn(name), { body })
  if (error) {
    let message = error.message
    let status: number | null = null
    try {
      const ctx = (error as { context?: Response }).context
      if (ctx) {
        status = ctx.status ?? null
        message = (await ctx.json()).error ?? message
      }
    } catch { /* keep the default message */ }
    throw new ApiError(message, status)
  }
  return data as T
}
const call = <T>(body: Record<string, unknown>) => callFn<T>('plan', body)

export const fetchPlan = (id: string) => call<{ plan: Plan; days: PlanDay[] }>({ get: id })
export const fetchMenu = (id: string) => call<Menu>({ menu: id })
const addCall = (plan: string, req: AddReq) => call<AddRes>({ add: { plan, ...req } })
const removeCall = (id: string) => call<{ ok: true }>({ remove: id })
const moveCall = (id: string, to: { day: string } | { start: string; end?: string }) => call<{ plan_item: AddRes['plan_item'] }>({ move: { id, ...to } })
export const updatePlan = (plan: string, patch: { title?: string; from?: string; to?: string; surprise?: boolean; status?: 'archived' }) => call<{ plan: Plan }>({ update: { plan, ...patch } })
export const markExported = (plan: string, method: 'google' | 'ics') => call<{ ok: true; recorded: number }>({ exported: plan, method })
export const sharePlan = (plan: string) => call<{ share_token: string }>({ share: plan })
export const unsharePlan = (plan: string) => call<{ share_token: null }>({ share: plan, off: true })
export const planLink = (token: string) => `${APP_URL}/p/${token}`

/** "Build it for me": a draft (nothing saved). `item` may arrive as the pool row or its id. */
export type DraftItem = {
  item: PlanItemData | string; start: string | null; end: string | null; approx: boolean; why: 'fit' | 'loved' | 'passion' | 'nearby' | string
  /** Already in the plan: shown in place so the day reads whole, never re-added or removable here. */
  existing?: boolean; window?: Window | null
}
export type Draft = { days: { date: string; items: DraftItem[] }[]; skipped_days?: string[] }
export const suggestPlan = (plan: string) => call<Draft>({ suggest: plan })
export const acceptPlan = (plan: string, picks: { item: string; day: string; start?: string; end?: string }[]) => call<Record<string, unknown>>({ accept: plan, picks })
export const draftItemId = (d: DraftItem) => (typeof d.item === 'string' ? d.item : d.item?.id)

/** A server addition that isn't deployed (404) or doesn't accept this yet (400): hide its control, quietly. */
export const notThere = (e: unknown) => e instanceof ApiError && (e.status === 404 || e.status === 400)
const OFF_KEY = 'mg_off'
export function featureOff(name: string): boolean {
  try { return (JSON.parse(sessionStorage.getItem(OFF_KEY) ?? '[]') as string[]).includes(name) } catch { return false }
}
export function turnOff(name: string) {
  try {
    const all = new Set(JSON.parse(sessionStorage.getItem(OFF_KEY) ?? '[]') as string[])
    all.add(name)
    sessionStorage.setItem(OFF_KEY, JSON.stringify([...all]))
  } catch { /* hidden for this render only */ }
}

/**
 * A shared plan, read with the public key only: no session is read or created, so someone opening the link never
 * becomes an anonymous user. null = the link was turned off or never existed.
 */
export async function fetchSharedPlan(token: string): Promise<SharedPlanData | null> {
  const r = await fetch(`${SUPABASE_URL}/functions/v1/${fn('plan')}`, {
    method: 'POST',
    headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ shared: token }),
  })
  if (r.status === 404 || r.status === 400) return null
  if (!r.ok) {
    let message = `plan ${r.status}`
    try { message = (await r.json()).error ?? message } catch { /* keep */ }
    throw new ApiError(message, r.status)
  }
  const data = (await r.json()) as SharedPlanData
  return data?.plan ? { plan: data.plan, days: Array.isArray(data.days) ? data.days : [], unscheduled: Array.isArray(data.unscheduled) ? data.unscheduled : [] } : null
}

// ---------- the shared plan store ----------
/** A local addition: shown at once, kept as a draft row if the server refuses it. */
export type Pending = {
  key: string; req: AddReq; itemId: string | null; title: string; category: string | null
  day: string | null; start: string | null; status: 'pending' | 'saved' | 'failed'; message?: string; planItemId?: string
  /** The time the card showed when it was added (null: it showed none). */
  shown?: string | null
}
/** The latest addition, for the one-line confirmation; `time` is set when it landed at a time the card didn't show. */
export type LastAdd = { title: string; at: number; time?: string; day?: string }
export type Entry = { plan: Plan | null; days: PlanDay[] | null; error: string | null; pending: Pending[]; busy: number; lastAdd: LastAdd | null }

const EMPTY: Entry = { plan: null, days: null, error: null, pending: [], busy: 0, lastAdd: null }
const store = new Map<string, Entry>()
const loads = new Map<string, Promise<void>>()
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())
const get = (id: string) => store.get(id) ?? EMPTY
function patch(id: string, fn: (e: Entry) => Partial<Entry>) {
  const cur = get(id)
  store.set(id, { ...cur, ...fn(cur) })
  emit()
}
/** The plan as stored right now (for reading back after an awaited change). */
export const peekPlan = (id: string): Entry => get(id)
const busy = (id: string, d: number) => patch(id, (e) => ({ busy: Math.max(0, e.busy + d) }))

/** Fetch (or refetch) the plan and its days. Concurrent callers share one request. */
const loadSeq = new Map<string, number>()
/**
 * Fetch the plan. `fresh` (after a change) never reuses a load already in flight, which may have left before the
 * change landed (R8: a first "Fix it" tap read the old times back); only the newest answer is applied.
 */
export function loadPlan(id: string, fresh = false): Promise<void> {
  const inflight = loads.get(id)
  if (inflight && !fresh) return inflight
  const n = (loadSeq.get(id) ?? 0) + 1
  loadSeq.set(id, n)
  const p: Promise<void> = fetchPlan(id)
    .then((res) => {
      if (loadSeq.get(id) !== n) return
      const ids = new Set((res.days ?? []).flatMap((d) => d.items.map((s) => s.id)))
      // the server's travel is from the stop before it in this order
      const days = (res.days ?? []).map((d) => ({ ...d, items: d.items.map((s, i) => ({ ...s, travelFrom: i > 0 ? d.items[i - 1].id : null })) }))
      // a saved addition stays a local row only until the server's list has it
      patch(id, (e) => ({ plan: res.plan, days, error: null, pending: e.pending.filter((x) => !(x.status === 'saved' && x.planItemId && ids.has(x.planItemId))) }))
    })
    .catch((e) => {
      if (loadSeq.get(id) !== n) return
      Sentry.captureException(e)
      patch(id, () => ({ error: errorMessage(e) || f2('common.error') }))
    })
    .finally(() => { if (loads.get(id) === p) loads.delete(id) })
  loads.set(id, p)
  return p
}

const subscribe = (cb: () => void) => {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

/** The plan as every screen sees it. Loads on first use; `reload` refetches. */
export function usePlan(id: string): Entry & { reload: () => void } {
  const entry = useSyncExternalStore(subscribe, () => get(id))
  useEffect(() => {
    if (id) void loadPlan(id)
  }, [id])
  return { ...entry, reload: () => void loadPlan(id) }
}

/** Every activity in the plan (saved or local), by pool item id: what a menu card shows as Added. */
export function addedItems(e: Entry): Map<string, { planItemId: string | null; status: 'saved' | 'pending' | 'failed'; key?: string; day: string | null }> {
  const out = new Map<string, { planItemId: string | null; status: 'saved' | 'pending' | 'failed'; key?: string; day: string | null }>()
  for (const d of e.days ?? []) for (const s of d.items) if (s.item?.id) out.set(s.item.id, { planItemId: s.id, status: 'saved', day: s.day })
  for (const p of e.pending) {
    if (!p.itemId || out.has(p.itemId)) continue
    out.set(p.itemId, { planItemId: p.planItemId ?? null, status: p.status === 'failed' ? 'failed' : p.status === 'saved' ? 'saved' : 'pending', key: p.key, day: p.day })
  }
  return out
}

/** Experiences per day, local additions included (day pickers, tabs). */
export function dayCounts(e: Entry): Map<string, number> {
  const m = new Map<string, number>()
  const ids = new Set<string>()
  for (const d of e.days ?? []) for (const s of d.items) { ids.add(s.id); m.set(d.date, (m.get(d.date) ?? 0) + 1) }
  for (const p of e.pending) if (p.day && !(p.planItemId && ids.has(p.planItemId))) m.set(p.day, (m.get(p.day) ?? 0) + 1)
  return m
}

/** The confirmation when an addition landed at a time its card didn't show: "Added for 6:45 PM" ("Sat 6:45 PM" on a longer plan). */
export function addedForLine(l: LastAdd, multiDay: boolean): string | null {
  if (!l.time) return null
  return f2('menu.added_for', { time: multiDay && l.day ? `${weekday(l.day)} ${clock(l.time)}` : clock(l.time) })
}

/** What is already on each day (saved and local), in time order: the day picker says it in words. */
export function dayTitles(e: Entry): Map<string, string[]> {
  const rows = new Map<string, { t: string; at: string }[]>()
  const ids = new Set<string>()
  const put = (day: string, t: string, at: string | null) => rows.set(day, [...(rows.get(day) ?? []), { t, at: at ?? '~' }])
  for (const d of e.days ?? []) for (const s of d.items) { ids.add(s.id); put(d.date, s.name, s.start) }
  for (const p of e.pending) if (p.day && p.status !== 'failed' && !(p.planItemId && ids.has(p.planItemId))) put(p.day, p.title, p.start)
  return new Map([...rows].map(([d, list]) => [d, list.sort((a, b) => a.at.localeCompare(b.at)).map((x) => x.t)]))
}

/** Number of experiences in the plan, local additions included. */
export function planCount(e: Entry): number {
  const ids = new Set((e.days ?? []).flatMap((d) => d.items.map((s) => s.id)))
  return ids.size + e.pending.filter((p) => !(p.planItemId && ids.has(p.planItemId))).length
}

let seq = 0
async function sendAdd(id: string, key: string) {
  const p = get(id).pending.find((x) => x.key === key)
  if (!p) return
  busy(id, 1)
  try {
    const res = await addCall(id, p.req)
    const row = res.plan_item
    // the card showed one time and the plan holds another (or the card showed none): say where it landed
    const moved = Boolean(row.start_at) && (!p.shown || Math.abs(new Date(row.start_at!).getTime() - new Date(p.shown).getTime()) >= 60e3)
    patch(id, (e) => ({
      pending: e.pending.map((x) => (x.key === key ? { ...x, status: 'saved' as const, planItemId: row.id, day: row.day, start: row.start_at } : x)),
      ...(moved ? { lastAdd: { title: p.title, at: Date.now(), time: row.start_at!, day: row.day } } : {}),
    }))
    track('plan_add', { plan: id, kind: p.req.item ? 'item' : p.req.stop ? 'stop' : 'custom', conflict: res.conflict })
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      // already in the plan (another tab, a partner, a double tap): the reload shows it
      patch(id, (s) => ({ pending: s.pending.filter((x) => x.key !== key) }))
    } else {
      if (!(e instanceof ApiError && e.status !== null && e.status < 500)) Sentry.captureException(e)
      patch(id, (s) => ({ pending: s.pending.map((x) => (x.key === key ? { ...x, status: 'failed' as const, message: errorMessage(e) || f2('menu.add_failed') } : x)) }))
    }
  } finally {
    busy(id, -1)
  }
  await loadPlan(id, true)
}

/** Optimistic add: the row appears now, the server confirms after. Repeated taps on one occurrence never duplicate. */
export function addToPlan(id: string, req: AddReq, meta: { title: string; category: string | null; itemId?: string | null; day?: string | null; start?: string | null; shown?: string | null }): string | null {
  const e = get(id)
  if (meta.itemId && addedItems(e).has(meta.itemId)) return null
  const key = `p${++seq}`
  const p: Pending = {
    key, req, itemId: meta.itemId ?? null, title: meta.title, category: meta.category, day: meta.day ?? req.day ?? null, start: meta.start ?? req.start ?? null, status: 'pending',
    shown: meta.shown === undefined ? (meta.start ?? req.start ?? null) : meta.shown,
  }
  patch(id, (s) => ({ pending: [...s.pending, p], lastAdd: { title: meta.title, at: Date.now() } }))
  void sendAdd(id, key)
  return key
}

export function retryAdd(id: string, key: string) {
  patch(id, (e) => ({ pending: e.pending.map((x) => (x.key === key ? { ...x, status: 'pending' as const, message: undefined } : x)) }))
  void sendAdd(id, key)
}

export function discardAdd(id: string, key: string) {
  patch(id, (e) => ({ pending: e.pending.filter((x) => x.key !== key) }))
}

/** What it takes to put a removed stop back (Undo). */
export function readdRequest(s: PlanStop): AddReq | null {
  const start = s.start ?? undefined
  if (s.item?.id) return { item: s.item.id, ...(start && !s.approx ? { start } : { day: s.day }) }
  if (s.stop?.id) return { stop: s.stop.id, ...(start ? { start } : { day: s.day }) }
  if (s.custom) return { custom: { title: s.custom.title, ...(s.custom.address ? { address: s.custom.address } : {}), ...(s.custom.url ? { url: s.custom.url } : {}), ...(s.custom.type ? { type: s.custom.type } : {}) }, ...(s.note ? { note: s.note } : {}), ...(start ? { start, ...(s.end ? { end: s.end } : {}) } : { day: s.day }) }
  return null
}

/** Remove one stop (optimistic). Throws on failure after restoring it. */
export async function removeStop(id: string, stopId: string): Promise<void> {
  const before = get(id).days
  patch(id, (e) => ({ days: e.days?.map((d) => ({ ...d, items: d.items.filter((s) => s.id !== stopId) })) ?? null }))
  busy(id, 1)
  try {
    await removeCall(stopId)
  } catch (e) {
    patch(id, () => ({ days: before }))
    throw e
  } finally {
    busy(id, -1)
  }
  await loadPlan(id, true)
}

/**
 * Move a stop to another day (the server picks a fitting time: the same day = "find it a free time") or to a start
 * time. Throws with the server's words. `background`: resolve once the server has the move; the refetch that brings
 * travel and conflicts finishes on its own (a sheet can close without waiting on it).
 */
export async function moveStop(id: string, stopId: string, to: { day: string } | { start: string; end?: string }, background = false): Promise<void> {
  const before = get(id).days
  if ('start' in to) {
    // show the new time now; the reload brings travel and conflicts
    patch(id, (e) => ({
      days: e.days?.map((d) => ({
        ...d,
        items: d.items.map((s) => {
          if (s.id !== stopId || !s.start) return s
          const dur = s.end ? new Date(s.end).getTime() - new Date(s.start).getTime() : 2 * 3600e3
          return { ...s, start: to.start, end: to.end ?? new Date(new Date(to.start).getTime() + dur).toISOString(), approx: false }
        }).sort((a, b) => String(a.start ?? '').localeCompare(String(b.start ?? ''))),
      })) ?? null,
    }))
  }
  busy(id, 1)
  try {
    await moveCall(stopId, to)
  } catch (e) {
    patch(id, () => ({ days: before }))
    throw e
  } finally {
    busy(id, -1)
  }
  if (background) void loadPlan(id, true)
  else await loadPlan(id, true)
}

/**
 * A drop-in visit with a new length. The server's move keeps a stop's length, so the visit is taken out and added back
 * at the new start and end (add takes an end and refuses anything outside the window); if that is refused, the visit
 * goes back as it was and the server's words are thrown.
 */
export async function revisit(id: string, s: PlanStop, start: string, end: string): Promise<void> {
  const item = s.item?.id
  if (!item) throw new Error(f2('common.error'))
  busy(id, 1)
  try {
    await removeCall(s.id)
    try {
      await addCall(id, { item, day: s.day, start, end })
    } catch (e) {
      await addCall(id, { item, day: s.day, ...(s.start ? { start: s.start } : {}), ...(s.end ? { end: s.end } : {}) }).catch((x) => Sentry.captureException(x))
      throw e
    }
  } finally {
    busy(id, -1)
    await loadPlan(id, true)
  }
}

export async function savePlanPatch(id: string, p: Parameters<typeof updatePlan>[1]): Promise<void> {
  busy(id, 1)
  try {
    const res = await updatePlan(id, p)
    patch(id, (e) => ({ plan: e.plan ? { ...e.plan, ...res.plan, mine: e.plan.mine } : res.plan }))
  } finally {
    busy(id, -1)
  }
}

export function setShareToken(id: string, token: string | null) {
  patch(id, (e) => ({ plan: e.plan ? { ...e.plan, share_token: token } : e.plan }))
}

// ---------- the menu (fetched once per plan per visit; filtered here) ----------
type MenuEntry = { menu: Menu | null; error: string | null; scouting: boolean; fresh: Set<string> }
const menus = new Map<string, MenuEntry>()
const menuLoads = new Map<string, Promise<void>>()
const MENU_EMPTY: MenuEntry = { menu: null, error: null, scouting: false, fresh: new Set() }
const setMenu = (id: string, p: Partial<MenuEntry>) => {
  menus.set(id, { ...(menus.get(id) ?? MENU_EMPTY), ...p })
  emit()
}

/**
 * Fold a fresh menu into the one on screen without reshuffling it: cards already showing keep their order, new
 * ones slot in where the new ranking puts them, and their ids come back so they can carry a "new" badge.
 */
function mergeMenu(old: Menu, next: Menu): { menu: Menu; added: string[] } {
  const added: string[] = []
  const fold = (prev: MenuCard[], cur: MenuCard[]) => {
    const curById = new Map(cur.map((c) => [c.item.id, c]))
    const out = prev.filter((c) => curById.has(c.item.id)).map((c) => curById.get(c.item.id)!)
    const had = new Set(prev.map((c) => c.item.id))
    cur.forEach((c, i) => {
      if (had.has(c.item.id)) return
      added.push(c.item.id)
      out.splice(Math.min(i, out.length), 0, c)
    })
    return out
  }
  const oldDays = new Map(old.days.map((d) => [d.date, d.items]))
  return {
    menu: { ...next, top: fold(old.top, next.top), days: next.days.map((d) => ({ ...d, items: fold(oldDays.get(d.date) ?? [], d.items) })), anytime: fold(old.anytime, next.anytime) },
    added: [...new Set(added)],
  }
}

const asMenu = (m: Menu): Menu => ({ covered: m.covered !== false, place: m.place, top: m.top ?? [], days: m.days ?? [], anytime: m.anytime ?? [], per_day: m.per_day ?? 2 })

/** Cards in `keep` (just turned down, still showing their Undo row) stay where they were when a re-rank drops them. */
function keepCards(old: Menu, next: Menu, keep: Set<string>): Menu {
  const hold = (prev: MenuCard[], cur: MenuCard[]) => {
    const out = [...cur]
    const there = new Set(cur.map((c) => c.item.id))
    prev.forEach((c, i) => {
      if (keep.has(c.item.id) && !there.has(c.item.id)) out.splice(Math.min(i, out.length), 0, c)
    })
    return out
  }
  const oldDays = new Map(old.days.map((d) => [d.date, d.items]))
  return { ...next, top: hold(old.top, next.top), days: next.days.map((d) => ({ ...d, items: hold(oldDays.get(d.date) ?? [], d.items) })), anytime: hold(old.anytime, next.anytime) }
}

/**
 * R8: after a "Not for me" with a reason, fetch the menu again (the server re-ranks from reasons) and swap it in.
 * Quiet and fail-soft: no spinner, no error banner, the old list stays on any failure. `beforeSwap` runs right before
 * the new list goes in (to remember the scroll anchor). Resolves true when the list was replaced.
 */
export async function rerankMenu(id: string, keep: Set<string>, beforeSwap?: () => void): Promise<boolean> {
  try {
    const m = await fetchMenu(id)
    const cur = menus.get(id)
    if (!cur?.menu || m.covered === false) return false
    beforeSwap?.()
    setMenu(id, { menu: keepCards(cur.menu, asMenu(m), keep) })
    return true
  } catch (e) {
    Sentry.captureException(e)
    return false
  }
}

export function loadMenu(id: string, force = false, merge = false): Promise<void> {
  const inflight = menuLoads.get(id)
  if (inflight && !force) return inflight
  const p = fetchMenu(id)
    .then((m) => {
      const next = asMenu(m)
      const cur = menus.get(id)
      if (merge && cur?.menu) {
        const { menu, added } = mergeMenu(cur.menu, next)
        setMenu(id, { menu, error: null, fresh: new Set([...cur.fresh, ...added]) })
      } else setMenu(id, { menu: next, error: null })
    })
    .catch((e) => {
      Sentry.captureException(e)
      setMenu(id, { error: errorMessage(e) || f2('menu.refresh_failed') })
    })
    .finally(() => menuLoads.delete(id))
  menuLoads.set(id, p)
  return p
}

// ---------- scout: fresh events on demand (POST /scout), polled quietly; fails soft ----------
const scouted = new Set<string>()
const scoutStart = (plan: string) => callFn<{ queued?: number }>('scout', { plan })
const scoutStatus = (plan: string) => callFn<{ running?: boolean; found_recent?: number }>('scout', { status: plan })

/** Once per plan per page load: ask for a fresh look, poll every 6s for up to 90s, fold new finds into the menu. */
export function runScout(id: string) {
  if (!id || scouted.has(id) || featureOff('scout')) return
  scouted.add(id)
  const t0 = Date.now()
  const stop = () => setMenu(id, { scouting: false })
  const poll = async () => {
    try {
      const s = await scoutStatus(id)
      if (!s?.running) {
        stop()
        if ((s?.found_recent ?? 0) > 0) await loadMenu(id, true, true)
        return
      }
    } catch (e) {
      if (notThere(e)) turnOff('scout')
      else Sentry.captureException(e)
      return stop()
    }
    if (Date.now() - t0 > 90e3) return stop()
    window.setTimeout(poll, 6000)
  }
  scoutStart(id)
    .then((r) => {
      if (!((r?.queued ?? 0) > 0)) return
      setMenu(id, { scouting: true })
      window.setTimeout(poll, 6000)
    })
    .catch((e) => {
      if (notThere(e)) turnOff('scout')
      else Sentry.captureException(e)
    })
}

/** A pool item from the menu already loaded for this plan (no fetch). */
export function peekMenuItem(planId: string, itemId: string): PlanItemData | null {
  const m = menus.get(planId)?.menu
  if (!m) return null
  return [...m.days.flatMap((d) => d.items), ...m.anytime].find((c) => c.item.id === itemId)?.item ?? null
}

export function useMenu(id: string): MenuEntry & { retry: () => void } {
  const m = useSyncExternalStore(subscribe, () => menus.get(id) ?? MENU_EMPTY)
  useEffect(() => {
    if (id && !menus.get(id)?.menu) void loadMenu(id)
  }, [id])
  return { ...m, retry: () => void loadMenu(id, true) }
}

// ---------- dates and times (destination time: the plan's own zone, Los Angeles by default) ----------
export const LA_TZ = 'America/Los_Angeles'
/**
 * WP13: a plan in another city formats its times in that city's zone (`plan.dest.tz`). The screen showing a plan
 * calls `usePlanZone(plan)` (or `setPlanZone`) before its children render; every helper below reads the current
 * zone, with formatters cached per zone. One plan is on screen at a time, so one current zone is enough.
 */
let zoneNow = LA_TZ
function validZone(tz: string | null | undefined): string {
  if (!tz) return LA_TZ
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return tz
  } catch {
    return LA_TZ
  }
}
let cityNow = 'Los Angeles'
/** The city link-outs search in ("See it" on TikTok, Maps...): the plan's city, Los Angeles by default. */
export const seeItCity = () => cityNow
export function setPlanZone(tz: string | null | undefined, cityLabel?: string | null) {
  zoneNow = validZone(tz)
  cityNow = cityLabel?.trim() || 'Los Angeles'
}
/** The current city's IANA zone (the screen on show set it). */
export const currentZone = () => zoneNow
/** The current zone's everyday name for "Times shown in ...": "Pacific Time", "Western European Time". */
export function zoneName(): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: zoneNow, timeZoneName: 'longGeneric' }).formatToParts(new Date()).find((x) => x.type === 'timeZoneName')
    return part?.value ?? zoneNow
  } catch {
    return zoneNow === LA_TZ ? 'Pacific Time' : zoneNow
  }
}
/** The plan's zone (Los Angeles unless the plan says otherwise). */
export const planZone = (plan: Plan | null | undefined) => validZone(plan?.dest?.tz)
/** Sets the current zone from this plan during render, so every time below it reads in the plan's city. */
export function usePlanZone(plan: Plan | null | undefined) {
  const la = !plan?.dest?.city || plan.dest.city === 'los-angeles'
  setPlanZone(plan?.dest?.tz, la ? null : plan?.dest?.label)
}
const fmtCache = new Map<string, Intl.DateTimeFormat>()
function zfmt(kind: 'ymd' | 'clock' | 'hhmm' | 'parts' | 'offset'): Intl.DateTimeFormat {
  const key = `${kind}|${zoneNow}`
  let f = fmtCache.get(key)
  if (!f) {
    const timeZone = zoneNow
    f =
      kind === 'ymd' ? new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
      : kind === 'clock' ? new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' })
      : kind === 'hhmm' ? new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      : kind === 'parts' ? new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit', hour12: true })
      : new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' })
    fmtCache.set(key, f)
  }
  return f
}
/** Minutes the current zone is ahead of UTC at this instant. */
function zoneOffsetMin(d: Date): number {
  const o: Record<string, number> = {}
  for (const x of zfmt('offset').formatToParts(d)) if (x.type !== 'literal') o[x.type] = Number(x.value)
  const asUtc = Date.UTC(o.year, o.month - 1, o.day, o.hour % 24, o.minute)
  return Math.round((asUtc - (d.getTime() - d.getUTCSeconds() * 1000 - d.getUTCMilliseconds())) / 60000)
}
/** The instant for a wall-clock time in the current zone (two passes, so a clock change between guess and answer settles). */
function zoneWallTime(y: number, m: number, d: number, h: number, min = 0): Date {
  const guess = Date.UTC(y, m - 1, d, h, min)
  let at = guess - zoneOffsetMin(new Date(guess)) * 60000
  at = guess - zoneOffsetMin(new Date(at)) * 60000
  return new Date(at)
}
const dayLong = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric' })
const dayMed = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' })
const monthDay = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })
const wkShort = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short' })
const wkLong = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'long' })
const noon = (ymd: string) => new Date(`${ymd}T12:00:00Z`)

/** The calendar day of an instant in the plan's zone, "2026-10-03". */
export const laYmd = (iso: string | Date) => zfmt('ymd').format(typeof iso === 'string' ? new Date(iso) : iso)
/** "7:30 PM", in the plan's zone. */
export const clock = (iso: string) => zfmt('clock').format(new Date(iso))
/** A start at exactly midnight LA is a date without a time (R7: a flea market listed at 12:00 AM): never shown or exported as a time. */
export const atMidnight = (iso: string | null | undefined): boolean => Boolean(iso) && zfmt('hhmm').format(new Date(iso!)) === '00:00'
/** "19:30", LA (for a time input). */
export const hhmm = (iso: string) => zfmt('hhmm').format(new Date(iso))
/** "Saturday, October 3". */
export const dayFull = (ymd: string) => dayLong.format(noon(ymd))
/** "Sat, Oct 3". */
export const dayShort = (ymd: string) => dayMed.format(noon(ymd))
/** "Sat". */
export const weekday = (ymd: string) => wkShort.format(noon(ymd))
export const weekdayLong = (ymd: string) => wkLong.format(noon(ymd))
/** "Oct 3". */
export const monthDayOf = (ymd: string) => monthDay.format(noon(ymd))
/** 0 = Sunday. */
export const dowOf = (ymd: string) => noon(ymd).getUTCDay()
export const addDays = (ymd: string, n: number) => {
  const d = noon(ymd)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}
export function daysBetween(a: string, b: string): string[] {
  const out: string[] = []
  for (let d = a; d <= b && out.length < 62; d = addDays(d, 1)) out.push(d)
  return out
}
/** "Sat, Oct 3" for one day; "Oct 3 – 12" or "Oct 30 – Nov 2" for a range. */
export function rangeLabel(from: string, to: string): string {
  if (from === to) return dayShort(from)
  const a = monthDayOf(from)
  const b = monthDayOf(to)
  return a.split(' ')[0] === b.split(' ')[0] ? `${a} – ${b.split(' ')[1]}` : `${a} – ${b}`
}
/** "7:30 PM – 9:30 PM". */
export const timeRange = (a: string, b: string | null) => (b ? `${clock(a)} – ${clock(b)}` : clock(a))
function hm(iso: string): { t: string; p: string } {
  const o: Record<string, string> = {}
  for (const x of zfmt('parts').formatToParts(new Date(iso))) o[x.type] = x.value
  return { t: o.minute === '00' ? o.hour : `${o.hour}:${o.minute}`, p: o.dayPeriod ?? '' }
}
/** "12–5 PM", "11:30 AM–1 PM": a short span for windows and visits. */
export function shortRange(a: string, b: string): string {
  const x = hm(a), y = hm(b)
  return x.p === y.p ? `${x.t}–${y.t} ${y.p}` : `${x.t} ${x.p}–${y.t} ${y.p}`
}
/** How long something takes, as people say it: "~45 min", "~2 hr". null when unknown or longer than a long day. */
export function lengthLabel(start: string | null | undefined, end: string | null | undefined): string | null {
  if (!start || !end) return null
  const m = (new Date(end).getTime() - new Date(start).getTime()) / 60e3
  if (!Number.isFinite(m) || m <= 0 || m > 10 * 60) return null
  const r5 = Math.max(5, Math.round(m / 5) * 5)
  // minutes until an hour, half hours as hours, other short visits in minutes ("~75 min", not "~1.5 hr")
  if (r5 < 60 || (r5 < 120 && r5 % 30 !== 0)) return f2('common.length_min', { minutes: r5 })
  const h = Math.round((m / 60) * 2) / 2
  return f2('common.length_hr', { hours: h % 1 ? h.toFixed(1) : String(h) })
}
/** A window's visit, in words: "Open 12–5 PM · your visit 12:00–1:00 PM". */
export function windowLine(w: Window, visit: { start: string | null; end: string | null } | null): string {
  const open = f2('menu.window', { hours: shortRange(w.open, w.close) })
  return visit?.start && visit.end ? `${open} · ${f2('menu.visit', { time: shortRange(visit.start, visit.end) })}` : open
}
/** The instant for "HH:MM" on a calendar day in the plan's zone. */
export function atLA(ymd: string, hm: string): string {
  const [y, m, d] = ymd.split('-').map(Number)
  const [h, min] = hm.split(':').map(Number)
  return zoneWallTime(y, m, d, h, min).toISOString()
}
/** LA hour of day, fractional. */
export function laHour(iso: string): number {
  const [h, m] = hhmm(iso).split(':').map(Number)
  return h + m / 60
}
const Q = 15 * 60e3
export const roundUpQuarter = (t: number) => Math.ceil(t / Q) * Q
export const roundDownQuarter = (t: number) => Math.floor(t / Q) * Q

export function placeOf(plan: Plan | null): string {
  return plan?.dest?.label || 'Los Angeles'
}
export function planTitle(plan: Plan): string {
  return plan.title?.trim() || f2('itin.default_name', { place: placeOf(plan), dateRange: rangeLabel(plan.start_date, plan.end_date) })
}

/** A fixed event: a sourced occurrence with its own time (cannot move). Mirrors the server's move rule. */
export function isFixed(s: PlanStop): boolean {
  if (s.window) return false
  const it = s.item
  if (!it || it.kind !== 'event' || !it.start_at) return false
  return !(it.end_at && new Date(it.end_at).getTime() - new Date(it.start_at).getTime() > 12 * 3600e3)
}

/** A dated, single-occurrence event card (adds in one tap). */
export function cardFixed(c: MenuCard): boolean {
  if (c.window) return false
  const it = c.item
  if (it.kind !== 'event' || !it.start_at) return false
  return !(it.end_at && new Date(it.end_at).getTime() - new Date(it.start_at).getTime() > 12 * 3600e3)
}

/**
 * A menu card's time, with how long it takes: a set event its own time; a drop-in window its opening hours and the
 * visit we'd suggest; anything else the suggested slot (or its listed hours when nothing fits a slot).
 */
export function cardWhen(c: MenuCard, withDay = true): { when: string | null; suggestion: string | null; length: string | null } {
  const it = c.item
  const sug = c.suggested
  if (c.window) {
    return { when: withDay ? `${dayShort(laYmd(c.window.open))} · ${windowLine(c.window, sug)}` : windowLine(c.window, sug), suggestion: null, length: sug ? lengthLabel(sug.start, sug.end) : null }
  }
  if (cardFixed(c) && it.start_at) {
    // a listing with a date and no time (00:00) shows its day only, never "12:00 AM"
    if (atMidnight(it.start_at)) return { when: withDay ? dayShort(laYmd(it.start_at)) : null, suggestion: null, length: null }
    return { when: withDay ? `${dayShort(laYmd(it.start_at))} · ${clock(it.start_at)}` : clock(it.start_at), suggestion: null, length: lengthLabel(it.start_at, it.end_at) ?? (sug && sug.start === it.start_at ? lengthLabel(sug.start, sug.end) : null) }
  }
  return {
    when: it.hours_text?.trim() || null,
    suggestion: sug && !atMidnight(sug.start) ? f2('menu.suggested', { time: `${dayShort(laYmd(sug.start))} · ${clock(sug.start)}` }) : null,
    length: sug ? lengthLabel(sug.start, sug.end) : null,
  }
}

// ---------- menu filters ----------
export const QUICK = ['free', 'outdoors', 'calm', 'active', 'hidden'] as const
export type Quick = (typeof QUICK)[number]
export const PARTS = ['morning', 'afternoon', 'evening', 'late'] as const
export type Part = (typeof PARTS)[number]
export const MENU_CATS = ['music', 'comedy', 'art', 'film', 'theater', 'talks', 'outdoors', 'class', 'community', 'market', 'fitness', 'nightlife', 'day-trip', 'food', 'drink', 'other'] as const

/**
 * One removable filter. Quick + category + part + day chips come from taps; `ask` is a request in their words: it
 * stands for the server's own results (R7: an Ask is never turned into separate filters).
 */
export type Chip =
  | { t: 'quick'; v: Quick }
  | { t: 'cat'; v: string }
  | { t: 'part'; v: Part }
  | { t: 'day'; v: string }
  | { t: 'like'; v: string; title: string }
  | { t: 'ask'; v: string }

export const chipKey = (c: Chip) => `${c.t}:${c.v}`
const CHIP_KINDS = new Set(['quick', 'cat', 'part', 'day', 'like', 'ask'])
/** Chips remembered from an older version of the menu (word and id chips) are dropped. */
export const knownChips = (chips: unknown): Chip[] => (Array.isArray(chips) ? chips.filter((c) => c && CHIP_KINDS.has((c as Chip).t) && typeof (c as Chip).v === 'string') : [])

export function chipLabel(c: Chip): string {
  switch (c.t) {
    case 'quick': return f2(`menu.filter.${c.v}`)
    case 'cat': return f2(`category.${c.v.replace('-', '_')}`)
    case 'part': return f2(`tap.when.${c.v}`)
    case 'day': return dayShort(c.v)
    case 'like': return f2('menu.similar', { activity: c.title })
    case 'ask': return c.v
  }
}

/** When the card would happen (its own time, else the suggested slot), for part-of-day filters. */
function cardTime(c: MenuCard): string | null {
  return cardFixed(c) ? c.item.start_at : (c.suggested?.start ?? null)
}

function partOf(iso: string): Part {
  const h = laHour(iso)
  if (h >= 21 || h < 4) return 'late'
  if (h >= 17) return 'evening'
  if (h >= 12) return 'afternoon'
  return 'morning'
}

function quickMatch(c: MenuCard, q: Quick): boolean {
  const it = c.item
  const tags = it.tags ?? []
  const dims = it.vibe?.dims ?? {}
  switch (q) {
    case 'free': return Boolean(it.is_free) || it.cost_tier === 'free'
    case 'outdoors': return it.category === 'outdoors' || tags.includes('outdoor') || tags.includes('nature') || tags.includes('hike') || tags.includes('beach')
    case 'calm': {
      // R8 (a punk show and Disney Hall stayed under Low-key): never a large venue, never energy >= 0.15
      if (it.size_hint === 'large') return false
      const e = dims.energy
      if (typeof e !== 'number') return tags.includes('chill')
      if (e >= 0.15) return false
      return e <= -0.2 || tags.includes('chill') || tags.includes('intimate')
    }
    case 'active': return tags.includes('active') || tags.includes('hands-on') || (dims.posture ?? 0) > 0.4
    case 'hidden': return it.iykyk_score >= 0.65 || tags.includes('under-the-radar')
  }
}

/** Quick chips must all hold; categories and parts are either-or within their kind. The Ask chip is its own list. */
export function matches(c: MenuCard, chips: Chip[], likeIds: Set<string> | null): boolean {
  const cats = chips.filter((x) => x.t === 'cat').map((x) => x.v)
  const parts = chips.filter((x) => x.t === 'part').map((x) => x.v as Part)
  if (likeIds && !likeIds.has(c.item.id)) return false
  for (const x of chips) if (x.t === 'quick' && !quickMatch(c, x.v)) return false
  if (cats.length && !cats.includes(c.item.category)) return false
  if (parts.length) {
    const t = cardTime(c)
    if (!t || !parts.includes(partOf(t))) return false
  }
  return true
}

/** The server's reading of an Ask, as a short phrase: "Find free outdoor things" -> "free outdoor things". */
export function askSummary(understood: string): string {
  const s = understood.trim().replace(/[.!]+$/, '').replace(/^(find( me)?|show( me)?|look(ing)? for|search( for)?|get( me)?|give me)\s+/i, '')
  return s ? s.charAt(0).toLowerCase() + s.slice(1) : understood
}

// ---------- calendar export: one file, one event per timed stop ----------
// calendar LOCATION (Alvin 10/08: "some calendar adds have the right address, but not all"): lib/calLocation.ts
let calCity: string | null = null
/** The city of the itinerary being sent to a calendar (its plan's dest.city), for a venue with no street address. */
export function setCalendarCity(city: string | null) { calCity = city }
const place = (s: PlanStop) => s.item
  ? calendarLocation({ ...s.item, address: s.address ?? s.item.address, city: calCity })
  : s.stop ? calendarLocation({ title: s.stop.name, address: s.address ?? s.stop.address, neighborhood: s.stop.neighborhood, city: calCity })
  : calendarLocation({ title: s.custom?.title ?? s.name, address: s.address ?? s.custom?.address ?? null, city: calCity })

/**
 * A calendar title people can read on a phone (R7): a listing title over 40 characters gives way to its venue
 * ("Museum of Jurassic Technology"), a meal reads "Dinner at Cento Pasta Bar". The full title goes in the notes.
 */
export function calendarTitle(s: PlanStop): string {
  const venue = s.item?.venue_name?.trim()
  if (s.name.length <= 40 || !venue) return s.name
  if ((s.item?.category ?? s.category) === 'food') {
    const h = s.start ? laHour(s.start) : 19
    return f2(h >= 16 ? 'calendar.title_dinner' : h >= 11 ? 'calendar.title_lunch' : 'calendar.title_brunch', { venue })
  }
  return venue
}

function details(s: PlanStop): string {
  const full = calendarTitle(s) !== s.name ? s.name : ''
  return [full, s.item?.one_line ?? '', s.item?.price_text ? `Price: ${s.item.price_text}` : '', s.url ? `Details: ${s.url}` : '', 'Planned with Magellan'].filter(Boolean).join('\n')
}

/** A stop as the shared calendar invite's event (lib/calendarInvite.ts, pin 596). */
export function inviteStop(s: PlanStop): InviteStop | null {
  if (!s.start) return null
  return { title: calendarTitle(s), start: s.start, end: s.end ?? new Date(new Date(s.start).getTime() + 2 * 3600e3).toISOString(), location: place(s), url: s.url, note: details(s), uid: `plan-${s.id}` }
}
/** An .ics with a VEVENT per stop, every guest on each (pin 596). UIDs are stable per plan stop, so re-importing updates
 *  rather than duplicates where supported. */
export function planIcs(stops: PlanStop[], now = new Date(), guests: string[] = []): Blob {
  const { ics } = buildCalendarInvite(stops.map(inviteStop).filter((x): x is InviteStop => Boolean(x)), { guests, now })
  return new Blob([ics], { type: 'text/calendar;charset=utf-8' })
}

/** iPhone / iPad Safari (iPadOS reports a Mac with touch); other iOS browsers keep the download path. */
export function iosSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const ios = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)
  return ios && /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|GSA\/|DuckDuckGo|Instagram|FBAN|FBAV/i.test(ua)
}

/**
 * R8, iOS Safari: open the calendar file itself (a text/calendar blob, no `download`) so iOS shows its own add sheet.
 * Throws when it can't (the caller falls back to the download). The URL lives a minute: the sheet reads it late.
 */
export function openPlanIcs(stops: PlanStop[], guests: string[] = []) {
  const url = URL.createObjectURL(planIcs(stops, undefined, guests))
  window.location.assign(url)
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export function downloadPlanIcs(stops: PlanStop[], name: string, guests: string[] = []) {
  const url = URL.createObjectURL(planIcs(stops, undefined, guests))
  const a = document.createElement('a')
  a.href = url
  a.download = `${name}.ics`
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** Google Calendar template link for one stop (opened by its own tap, never in a burst), guests pre-filled (pin 596). */
export function googleStopUrl(s: PlanStop, guests: string[] = []): string {
  const x = inviteStop(s)
  return (x && googleInviteUrl(x, guests, zoneNow)) ?? 'https://calendar.google.com/calendar/render'
}

/** What the itinerary looked like when it was last exported, so later edits can say the calendar copy is stale. */
const EXPORT_KEY = 'mg_plan_export'
export const fingerprint = (days: PlanDay[] | null) => (days ?? []).flatMap((d) => d.items.map((s) => `${s.id}@${s.start ?? ''}`)).sort().join('|')
export function readExport(planId: string): string | null {
  try { return (JSON.parse(localStorage.getItem(EXPORT_KEY) ?? '{}') as Record<string, string>)[planId] ?? null } catch { return null }
}
export function writeExport(planId: string, fp: string) {
  try {
    const all = JSON.parse(localStorage.getItem(EXPORT_KEY) ?? '{}') as Record<string, string>
    all[planId] = fp
    localStorage.setItem(EXPORT_KEY, JSON.stringify(all))
  } catch { /* the stale notice is a convenience */ }
}

// ---------- scheduling helpers (itinerary) ----------
/** Straight-line km (same formula as the server). */
function kmBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371, rad = Math.PI / 180
  const dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(x))
}
/**
 * How this plan gets around, from what they told us (plan.params.transport): 'drive' with a car or nothing said,
 * else 'ride' with rideshare, else 'transit'. Nothing said = 'drive' (Alvin 10/08: assume a car).
 */
export function travelMode(plan: Plan | null | undefined): Exclude<Leg, 'walk'> | null {
  // Alvin 10/08: assume people have cars; nobody is asked how they get around. A plan that said otherwise keeps it.
  const t = plan?.params?.transport
  if (!Array.isArray(t)) return 'drive'
  const said = t.map(String)
  if (!said.length || said.includes('car')) return 'drive'
  if (said.includes('rideshare')) return 'ride'
  return 'transit'
}
/** About how long between two stops (same estimate the server uses); null when either has no location. */
export function legBetween(a: Pick<PlanStop, 'lat' | 'lng'>, b: Pick<PlanStop, 'lat' | 'lng'>, mode: Exclude<Leg, 'walk'> | null = 'drive'): (Travel & { known: boolean }) | null {
  if (a.lat == null || a.lng == null || b.lat == null || b.lng == null) return null
  const d = kmBetween({ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng })
  if (d <= 1) return { leg: 'walk', leg_min: Math.max(2, Math.round((d * 1.3) / 5 * 60)), known: true }
  const drive = Math.round((d <= 5 ? d * 3 : 15 + (d - 5) * 1.4) + 10)
  if (mode === 'ride') return { leg: 'ride', leg_min: drive + 5, known: true }
  if (mode === 'transit') return { leg: 'transit', leg_min: drive * 2 + 10, known: true }
  return { leg: 'drive', leg_min: drive, known: mode === 'drive' }
}
/** "About 12 min walk / drive / by ride / by transit"; a client estimate with no known mode never says drive. */
export function travelLine(t: Travel & { known?: boolean }): string {
  const minutes = Math.max(1, Math.round(t.leg_min))
  if (t.known === false && t.leg !== 'walk') return f2('itin.travel_any', { minutes })
  const key = { walk: 'itin.travel_walk', drive: 'itin.travel_drive', ride: 'itin.travel_ride', transit: 'itin.travel_transit' }[t.leg] ?? 'itin.travel_any'
  return f2(key, { minutes })
}
const durOf = (s: PlanStop) => (s.start && s.end ? new Date(s.end).getTime() - new Date(s.start).getTime() : 2 * 3600e3)

/**
 * Where a flexible stop lands when placed between two others: after `prev` with travel and a 15-minute buffer,
 * else ending before `next` with the same allowance. Quarter hours, like people plan. null = nothing to anchor to.
 */
export function startBetween(moving: PlanStop, prev: PlanStop | null | undefined, next: PlanStop | null | undefined): string | null {
  if (prev?.end) {
    const leg = legBetween(prev, moving)?.leg_min ?? 0
    return new Date(roundUpQuarter(new Date(prev.end).getTime() + Math.max(15, leg) * 60e3)).toISOString()
  }
  if (next?.start) {
    const leg = legBetween(moving, next)?.leg_min ?? 0
    return new Date(roundDownQuarter(new Date(next.start).getTime() - durOf(moving) - Math.max(15, leg) * 60e3)).toISOString()
  }
  return null
}

const EVENING = new Set(['music', 'comedy', 'theater', 'film', 'talks', 'nightlife', 'drink', 'food', 'art', 'community', 'class'])
/** The stop an evening can be built around: the last timed activity of the day that starts from 4pm and has a place. */
const isDinner = (s: PlanStop) => {
  if (!s.start) return false
  const h = laHour(s.start)
  const cat = s.item?.category ?? s.stop?.kind ?? s.category
  return h >= 16 && h < 22 && (cat === 'food' || cat === 'dinner' || /\bdinner\b/i.test(s.name))
}

/** R8: an evening that already has dinner plus a stop after 6 PM, or 3+ stops from 4 PM, needs no "Fill this evening". */
export function eveningFull(stops: PlanStop[]): boolean {
  const eve = stops.filter((s) => s.start && !atMidnight(s.start) && laHour(s.start) >= 16)
  if (eve.length >= 3) return true
  const dinner = eve.find(isDinner)
  return Boolean(dinner && eve.some((s) => s !== dinner && laHour(s.start!) >= 18))
}

export function eveningAnchor(stops: PlanStop[]): PlanStop | null {
  const c = stops.filter((s) => s.kind === 'item' && s.item && s.start && s.lat != null && s.lng != null && EVENING.has(s.item.category) && laHour(s.start) >= 16)
  return c[c.length - 1] ?? null
}
